import React, { useState, useEffect, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { UserProfile, ExamType } from '../../types/profile';
import { getMyProfile, saveMyProfile, getAllExams, getSubjectsForExam } from '../../components/apiClient.js';
import  ProfileNavigation  from './ProfileNavigation';

interface SubjectScoresFormData {
    subject_scores: Record<string, number | null>;
}

const SubjectScores: React.FC = () => {
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [examTypes, setExamTypes] = useState<ExamType[]>([]);
    const [availableSubjects, setAvailableSubjects] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [lastSaved, setLastSaved] = useState<Date | null>(null);

    // Initialize React Hook Form
    const {
        control,
        handleSubmit,
        watch,
        formState: { isValid },
        setValue,
        reset
    } = useForm<SubjectScoresFormData>({
        defaultValues: {
            subject_scores: {}
        },
        mode: 'onChange'
    });

    // Watch form values for real-time updates
    const formData = watch();

    // Load profile data and exam types on mount
    useEffect(() => {
        const loadData = async () => {
            try {
               // setLoading(true);
                // Load profile data
                const profileResponse = await getMyProfile();
                if (profileResponse.status === 'success' && profileResponse.result) {
                    const profileData = profileResponse.result;
                    setProfile(profileData);
                    reset({
                        subject_scores: profileData.subject_scores || {}
                    });
                }
                // Load exam types
                const examTypesResponse = await getAllExams();
                if (examTypesResponse.status === 'success' && examTypesResponse.result) {
                    setExamTypes(examTypesResponse.result || []);
                }
            } catch (error) {
                console.error('Failed to load profile data:', error);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [reset]);

    // Fetch subjects when profile.exam_type_id is available
    useEffect(() => {
        const fetchSubjects = async () => {
            if (profile && profile.exam_type_id) {
                //setLoading(true);
                try {
                    const subjectsResponse = await getSubjectsForExam(profile.exam_type_id);
                    if (subjectsResponse.status === 'success' && subjectsResponse.result) {
                        setAvailableSubjects(subjectsResponse.result || []);
                    } else {
                        setAvailableSubjects([]);
                    }
                } catch (error) {
                    setAvailableSubjects([]);
                    console.error('Failed to fetch subjects:', error);
                } finally {
                    setLoading(false);
                }
            } else {
                setAvailableSubjects([]);
            }
        };
        fetchSubjects();
    }, [profile?.exam_type_id]);

    // Removed unused getMaxScoreForSubject function

    // Get subjects based on selected exam type
    const getSubjects = useCallback(() => {
        if (!profile?.exam_type_id) return [];
        // Use subjects returned by getSubjectsForExam
        return availableSubjects.map(subject => ({
            id: subject.subject_id.toString(),
            name: subject.subject_name,
            maxScore: parseInt(subject.max_score)
        }));
    }, [availableSubjects, profile?.exam_type_id]);

    // Get exam name for display
    const getExamName = useCallback(() => {
        if (!profile?.exam_type_id) return 'Unknown Exam';
        const selectedExam = examTypes.find(exam => exam.id === profile.exam_type_id);
        return selectedExam?.exam_name || 'Unknown Exam';
    }, [examTypes, profile?.exam_type_id]);

    // Get max total score
    const getMaxTotalScore = useCallback(() => {
        if (!profile?.exam_type_id) return 0;
        const selectedExam = examTypes.find(exam => exam.id === profile.exam_type_id);

        if (!selectedExam) return 0;
        return selectedExam.max_score || 0;
        
    }, [examTypes, profile?.exam_type_id]);

    // Form submission handler
    const onSubmit = async (data: SubjectScoresFormData) => {
        if (isSaving) return;

        try {
            setIsSaving(true);

            // Merge form data with existing profile to prevent data loss
            const updatedProfile = {
                ...profile,
                ...data
            };

            const response = await saveMyProfile(updatedProfile);

            if (response.status === 'success') {
                setLastSaved(new Date());

                // Update local profile state
                setProfile(updatedProfile as UserProfile);
            } else {
                throw new Error(response.message || 'Failed to save profile');
            }
        } catch (error) {
            console.error('Save failed:', error);
        } finally {
            setIsSaving(false);
        }
    };

    // Update subject score using React Hook Form
    const updateSubjectScore = (subjectId: string, score: number | null) => {
        const currentScores = formData.subject_scores;
        setValue('subject_scores', {
            ...currentScores,
            [subjectId]: score
        });
    };

    const getScoreColor = (score: number, maxScore: number) => {
        const percentage = (score / maxScore) * 100;
        if (percentage >= 80) return 'success';
        if (percentage >= 60) return 'info';
        if (percentage >= 40) return 'warning';
        return 'danger';
    };

    const subjects = getSubjects();

    if (loading) {
        return (
            <div className="container-fluid px-4 py-3">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading profile data...</p>
                </div>
            </div>
        );
    }
   

    const totalEnteredScore = Object.values(formData.subject_scores || {}).reduce((sum: number, score) => {
        return sum + (score || 0);
    }, 0);

    // If user is beginner, show message and do not show form
    if (profile?.current_level === 'beginner') {
        return (
            <div className="min-vh-100" style={{ backgroundColor: '#f8f9ff', paddingTop: '10px' }}>
                <div className="container py-5">
                    <div className="row justify-content-center">
                        <div className="col-md-10 col-lg-12">
                            <ProfileNavigation className="mb-4" />
                            <div className="card shadow-sm border-0" style={{ borderRadius: '15px' }}>
                                <div className="card-body text-center p-5">
                                    <i className="fas fa-info-circle text-primary mb-3" style={{ fontSize: '3rem' }}></i>
                                    <h4 className="text-primary mb-3">No Subject Scores Needed</h4>
                                    <p className="text-muted mb-4">
                                        As a beginner, you have not attempted any exams yet. Subject-wise scores will be available after you attempt your first exam.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-vh-100" style={{ backgroundColor: '#f8f9ff', paddingTop: '10px' }}>
            <div className="container py-5">
                <div className="row justify-content-center">
                    <div className="col-md-10 col-lg-12">
                        <ProfileNavigation className="mb-4" />
                        {!profile?.exam_type_id && 
                            <div className="card shadow-sm border-0" style={{ borderRadius: '15px' }}>
                            <div className="card-body text-center p-5">
                                <i className="fas fa-info-circle text-warning mb-3" style={{ fontSize: '3rem' }}></i>
                                <h4 className="text-warning mb-3">Exam Type Required</h4>
                                <p className="text-muted mb-4">
                                    Please select your target exam type in Basic Information before setting up subject scores.
                                </p>
                                <a href="/profile/basic-info" className="btn btn-primary">
                                    <i className="fas fa-arrow-left me-2"></i>
                                    Go to Basic Information
                                </a>
                            </div>
                        </div>
                        }
                        <div className="card shadow-sm border-0" style={{ borderRadius: '15px' }}>
                            <div className="card-header bg-gradient bg-info text-white" style={{ borderRadius: '15px 15px 0 0' }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h4 className="mb-0 fw-bold">
                                        <i className="fas fa-chart-bar me-2"></i>
                                        Subject-wise Scores
                                    </h4>
                                    {lastSaved && (
                                        <small className="text-white-50">
                                            <i className="fas fa-check me-1"></i>
                                            Saved {lastSaved.toLocaleTimeString()}
                                        </small>
                                    )}
                                </div>
                                <p className="mb-0 text-white-75">
                                    Enter your previous scores for {getExamName()} to track your performance
                                </p>
                            </div>

                            <div className="card-body p-4">
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    {/* Score Overview */}
                                    <div className="row mb-4">
                                        <div className="col-md-6">
                                            <div className="card bg-primary text-white">
                                                <div className="card-body text-center">
                                                    <h5 className="mb-1">Total Score Entered</h5>
                                                    <h2 className="mb-0">{totalEnteredScore} / {getMaxTotalScore()}</h2>
                                                    <small>
                                                        {getMaxTotalScore() > 0 ? (((totalEnteredScore || 0) / getMaxTotalScore()) * 100).toFixed(1) : 0}% of maximum
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="card bg-info text-white">
                                                <div className="card-body text-center">
                                                    <h5 className="mb-1">Exam Type</h5>
                                                    <h2 className="mb-0">{getExamName()}</h2>
                                                    <small>{subjects.length} subjects</small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Subject Scores */}
                                    <div className="row g-4">
                                        {subjects.map(subject => (
                                            <div key={subject.id} className="col-md-6 col-lg-4">
                                                <div className="card border-primary">
                                                    <div className="card-header bg-primary text-white">
                                                        <h6 className="mb-0 fw-bold">
                                                            <i className="fas fa-atom me-2"></i>
                                                            {subject.name}
                                                        </h6>
                                                    </div>
                                                    <div className="card-body">
                                                        <div className="mb-3">
                                                            <label className="form-label fw-medium">Score</label>
                                                            <Controller
                                                                name={`subject_scores.${subject.id}` as any}
                                                                control={control}
                                                                rules={{
                                                                    min: { value: 0, message: `${subject.name} score cannot be negative` },
                                                                    max: { value: subject.maxScore, message: `${subject.name} score cannot exceed ${subject.maxScore}` }
                                                                }}
                                                                render={({ field, fieldState }) => (
                                                                    <>
                                                                        <div className="input-group">
                                                                            <input
                                                                                {...field}
                                                                                type="number"
                                                                                className={`form-control ${fieldState.error ? 'is-invalid' : ''}`}
                                                                                value={field.value || ''}
                                                                                onChange={(e) => {
                                                                                    const value = e.target.value ? parseInt(e.target.value) : null;
                                                                                    field.onChange(value);
                                                                                    updateSubjectScore(subject.id, value);
                                                                                }}
                                                                                placeholder="Enter score"
                                                                                min="0"
                                                                                max={subject.maxScore}
                                                                                style={{ borderRadius: '8px 0 0 8px' }}
                                                                            />
                                                                            <span className="input-group-text">/ {subject.maxScore}</span>
                                                                        </div>
                                                                        {fieldState.error && (
                                                                            <div className="invalid-feedback d-block">
                                                                                {fieldState.error.message}
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                )}
                                                            />
                                                        </div>

                                                        {/* Score Progress */}
                                                        {formData.subject_scores[subject.id] !== null && formData.subject_scores[subject.id] !== undefined && (
                                                            <div className="mt-3">
                                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                                    <small className="text-muted">Performance</small>
                                                                    <small className={`text-${getScoreColor(formData.subject_scores[subject.id]!, subject.maxScore)}`}>
                                                                        {((formData.subject_scores[subject.id]! / subject.maxScore) * 100).toFixed(1)}%
                                                                    </small>
                                                                </div>
                                                                <div className="progress" style={{ height: '8px' }}>
                                                                    <div
                                                                        className={`progress-bar bg-${getScoreColor(formData.subject_scores[subject.id]!, subject.maxScore)}`}
                                                                        style={{
                                                                            width: `${(formData.subject_scores[subject.id]! / subject.maxScore) * 100}%`,
                                                                            borderRadius: '4px'
                                                                        }}
                                                                    ></div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Performance Analytics */}
                                    {Object.values(formData.subject_scores).some(score => score !== null && score !== undefined) && (
                                        <div className="mt-5">
                                            <h5 className="mb-3">
                                                <i className="fas fa-analytics me-2 text-primary"></i>
                                                Performance Summary
                                            </h5>
                                            <div className="row g-3">
                                                {subjects.map(subject => {
                                                    const score = formData.subject_scores[subject.id];
                                                    if (score === null || score === undefined) return null;

                                                    return (
                                                        <div key={subject.id} className="col-md-6 col-lg-4">
                                                            <div className="card bg-light">
                                                                <div className="card-body text-center">
                                                                    <h6 className="text-muted mb-1">{subject.name}</h6>
                                                                    <h4 className={`text-${getScoreColor(score, subject.maxScore)} mb-1`}>
                                                                        {score} / {subject.maxScore}
                                                                    </h4>
                                                                    <small className="text-muted">
                                                                        {((score / subject.maxScore) * 100).toFixed(1)}% achieved
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Progress Indicators */}
                                    <div className="mt-4">
                                        <div className="card bg-light border-0" style={{ borderRadius: '12px' }}>
                                            <div className="card-body p-3">
                                                <div className="row g-3">
                                                    <div className="col-md-3">
                                                        <div className="d-flex align-items-center justify-content-center">
                                                            <div className={`rounded-circle me-2 ${profile?.exam_type_id ? 'bg-success' : 'bg-secondary'}`}
                                                                style={{ width: '12px', height: '12px' }}></div>
                                                            <small>Exam Selected</small>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <div className="d-flex align-items-center justify-content-center">
                                                            <div className={`rounded-circle me-2 ${(totalEnteredScore || 0) > 0 ? 'bg-success' : 'bg-warning'}`}
                                                                style={{ width: '12px', height: '12px' }}></div>
                                                            <small>Scores Entered</small>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <div className="d-flex align-items-center justify-content-center">
                                                            <div className={`rounded-circle me-2 ${isValid ? 'bg-success' : 'bg-danger'}`}
                                                                style={{ width: '12px', height: '12px' }}></div>
                                                            <small>Valid Data</small>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <div className="d-flex align-items-center justify-content-center">
                                                            <div className={`rounded-circle me-2 ${lastSaved ? 'bg-success' : 'bg-secondary'}`}
                                                                style={{ width: '12px', height: '12px' }}></div>
                                                            <small>Saved</small>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Save Button */}
                                    <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                                        <div className="d-flex align-items-center">
                                            {lastSaved && (
                                                <small className="text-muted">
                                                    <i className="fas fa-check text-success me-1"></i>
                                                    Last saved: {lastSaved.toLocaleTimeString()}
                                                </small>
                                            )}
                                        </div>
                                        <button
                                            type="submit"
                                            className={`btn btn-primary btn-lg ${isSaving ? 'disabled' : !isValid ? 'disabled' : ''
                                                }`}
                                            disabled={isSaving || !isValid}
                                        >
                                            {isSaving ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                    Saving...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-save me-2"></i>
                                                    Save Changes
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SubjectScores;